import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../auth/authContext';
const BASE_URL = import.meta.env.VITE_API_URL;
async function changePassword(currentPassword, newPassword) {
    const token = localStorage.getItem('glazier_access_token');
    const res = await fetch(`${BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    const json = await res.json();
    if (!json.success) {
        throw new Error(json.error ?? 'Failed to change password');
    }
}
export const MyAccount = () => {
    const { user } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changing, setChanging] = useState(false);
    const [changeError, setChangeError] = useState(null);
    const [changeSuccess, setChangeSuccess] = useState(false);
    const handleChangePassword = async (e) => {
        e.preventDefault();
        setChangeError(null);
        setChangeSuccess(false);
        if (newPassword.length < 8) {
            setChangeError('New password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setChangeError('New passwords do not match.');
            return;
        }
        setChanging(true);
        try {
            await changePassword(currentPassword, newPassword);
            setChangeSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
        catch (err) {
            setChangeError(err.message ?? 'Failed to change password');
        }
        finally {
            setChanging(false);
        }
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "My Account" }), _jsx("p", { style: styles.subheading, children: "Manage your profile and preferences" }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Profile Information" }), _jsxs("div", { style: styles.infoGrid, children: [_jsxs("div", { style: styles.infoItem, children: [_jsx("label", { style: styles.label, children: "User ID" }), _jsx("p", { style: styles.value, children: user?.userId || 'N/A' })] }), _jsxs("div", { style: styles.infoItem, children: [_jsx("label", { style: styles.label, children: "Username" }), _jsx("p", { style: styles.value, children: user?.username || 'N/A' })] }), _jsxs("div", { style: styles.infoItem, children: [_jsx("label", { style: styles.label, children: "Email" }), _jsx("p", { style: styles.value, children: user?.email || 'N/A' })] }), _jsxs("div", { style: styles.infoItem, children: [_jsx("label", { style: styles.label, children: "Account Status" }), _jsx("p", { style: styles.value, children: "Active" })] })] })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Change Password" }), _jsxs("form", { onSubmit: handleChangePassword, style: styles.passwordForm, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.fieldLabel, children: "Current Password" }), _jsx("input", { type: "password", style: styles.input, value: currentPassword, onChange: (e) => setCurrentPassword(e.target.value), placeholder: "Enter current password", disabled: changing, required: true, autoComplete: "current-password" })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.fieldLabel, children: "New Password" }), _jsx("input", { type: "password", style: styles.input, value: newPassword, onChange: (e) => {
                                                setNewPassword(e.target.value);
                                                setChangeError(null);
                                                setChangeSuccess(false);
                                            }, placeholder: "At least 8 characters", disabled: changing, required: true, autoComplete: "new-password" })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.fieldLabel, children: "Confirm New Password" }), _jsx("input", { type: "password", style: styles.input, value: confirmPassword, onChange: (e) => {
                                                setConfirmPassword(e.target.value);
                                                setChangeError(null);
                                                setChangeSuccess(false);
                                            }, placeholder: "Re-enter new password", disabled: changing, required: true, autoComplete: "new-password" })] }), changeError && (_jsx("div", { style: styles.errorBanner, children: changeError })), changeSuccess && (_jsx("div", { style: styles.successBanner, children: "Password changed successfully." })), _jsx("div", { style: styles.formActions, children: _jsx("button", { type: "submit", style: { ...styles.submitButton, opacity: changing ? 0.6 : 1 }, disabled: changing || !currentPassword || !newPassword || !confirmPassword, children: changing ? 'Changing…' : 'Change Password' }) })] })] })] }) }));
};
const styles = {
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: '0 0 40px 0',
        color: '#86868b',
    },
    card: {
        padding: '32px',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        marginBottom: '24px',
    },
    sectionTitle: {
        margin: '0 0 24px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#1d1d1f',
        letterSpacing: '-0.3px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '32px',
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontWeight: '600',
        color: '#1d1d1f',
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    value: {
        margin: 0,
        color: '#86868b',
        fontSize: '16px',
        fontWeight: '400',
        wordBreak: 'break-all',
    },
    passwordForm: {
        maxWidth: '400px',
    },
    field: {
        marginBottom: '16px',
    },
    fieldLabel: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
        letterSpacing: '-0.1px',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    errorBanner: {
        padding: '10px 14px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    successBanner: {
        padding: '10px 14px',
        backgroundColor: 'rgba(52,199,89,0.1)',
        border: '1px solid rgba(52,199,89,0.3)',
        borderRadius: '10px',
        color: '#248a3d',
        fontSize: '14px',
        marginBottom: '16px',
    },
    formActions: {
        marginTop: '8px',
    },
    submitButton: {
        padding: '12px 28px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
};
//# sourceMappingURL=MyAccount.js.map